import _ from 'lodash';

type EntityDetailsContent = Pick<
  Studio.ChartContent,
  'visType' | 'description'
> & { table?: Studio.TableOptions };

type Row = VisualisationDefinitions.EntityInformationTableRow;

// Whether a Table shows an entity page's entity, saved as the legacy Entity
// Information Table
export const isEntityDetailsTable = (content: EntityDetailsContent): boolean =>
  content.visType === 'table' && content.table?.rows === 'entityDetails';

const isCompleteRow = (row: Row): boolean =>
  row.type === 'field' ? !!row.datasetId && !!row.field : !!row.metricId;

// The metrics an entity details Table shows, in row order
export const getEntityDetailsMetricIds = (
  content: EntityDetailsContent,
): string[] =>
  isEntityDetailsTable(content)
    ? _.uniq(
        (content.table?.entityDetails?.rows ?? []).flatMap((row) =>
          row.type === 'metric' && row.metricId ? [row.metricId] : [],
        ),
      )
    : [];

// The datasets an entity details Table's field rows read
export const getEntityDetailsDataTypes = (
  content: EntityDetailsContent,
): string[] =>
  isEntityDetailsTable(content)
    ? _.uniq(
        (content.table?.entityDetails?.rows ?? []).flatMap((row) =>
          row.type === 'field' && row.datasetId ? [row.datasetId] : [],
        ),
      )
    : [];

// A report has no popups, so its rows open none (as copying a dashboard
// gadget to a report drops them)
const withoutDrillDowns = (row: Row): Row =>
  _.omit(row, ['reportDrillDownId', 'boardDrillDownId']) as Row;

// An entity details Table's options without its rows' popups
export const withoutEntityDetailsPopups = (
  table: Studio.TableOptions | undefined,
): Studio.TableOptions | undefined =>
  table && table.entityDetails && table.entityDetails.rows
    ? {
        ...table,
        entityDetails: {
          ...table.entityDetails,
          rows: table.entityDetails.rows.map(withoutDrillDowns),
        },
      }
    : table;

// The legacy Entity Information Table an entity details Table saves as and
// draws with (components/EntityInformationTable), with every setting
// written as the classic editor writes it
const buildEntityInformationTableDefinition = ({
  card,
  id,
  name,
  isReport = false,
}: {
  card: EntityDetailsContent;
  id: string;
  name: string;
  isReport?: boolean;
}):
  | { chartDefinition: VisualisationDefinitions.EntityInformationTable }
  | { missing: string } => {
  const settings = card.table?.entityDetails ?? {};
  const rows = settings.rows ?? [];
  if (rows.length === 0) {
    return { missing: 'Add a field or a metric' };
  }
  if (!rows.every(isCompleteRow)) {
    return { missing: 'Choose a field or metric for every row' };
  }
  return {
    chartDefinition: {
      id,
      name,
      description: card.description ?? '',
      type: 'EntityInformationTable',
      rows: isReport ? rows.map(withoutDrillDowns) : rows,
      datasetSorts: settings.datasetSorts ?? {},
      columnsPerRow: settings.columnsPerRow ?? 1,
      renderMode: settings.renderMode ?? 'cards',
    },
  };
};

const compact = <T extends object>(value: T): T | undefined => {
  const compacted = _.omitBy(value, _.isUndefined);
  return _.isEmpty(compacted) ? undefined : (compacted as T);
};

// An entity details Table's settings from a saved Entity Information Table
export const guessEntityDetailsCard = (
  chartDef: VisualisationDefinitions.EntityInformationTable,
): Studio.GadgetSidecarCard => ({
  visType: 'table',
  name: chartDef.name,
  ...(chartDef.description ? { description: chartDef.description } : {}),
  table: {
    rows: 'entityDetails',
    entityDetails: {
      rows: chartDef.rows,
      ...compact({
        datasetSorts: _.isEmpty(chartDef.datasetSorts)
          ? undefined
          : chartDef.datasetSorts,
        renderMode:
          chartDef.renderMode === 'grid' ? ('grid' as const) : undefined,
        columnsPerRow: chartDef.columnsPerRow === 2 ? (2 as const) : undefined,
      }),
    },
  },
});

// Whether the settings rebuild the saved Entity Information Table. Its name
// is the gadget's own, as is what it keeps for soft deletes; defaults
// written or left out are the same, and a version saved on it is read by
// nothing.
export const reproducesEntityInformationTable = ({
  card,
  chartDef,
  isReportGadget,
}: {
  card: Studio.GadgetSidecarCard;
  chartDef: VisualisationDefinitions.EntityInformationTable;
  isReportGadget: boolean;
}): boolean => {
  if (!isEntityDetailsTable(card)) {
    return false;
  }
  const built = buildEntityInformationTableDefinition({
    card,
    id: chartDef.id,
    name: chartDef.name,
    isReport: isReportGadget,
  });
  const comparable = (
    def: VisualisationDefinitions.EntityInformationTable,
  ) => ({
    ..._.omit(def, ['isHidden', 'hiddenOn', 'version']),
    description: def.description || undefined,
    rows: def.rows.map((row) =>
      _.omitBy(row, (value) => value === undefined || value === ''),
    ),
    datasetSorts: _.isEmpty(def.datasetSorts) ? undefined : def.datasetSorts,
    columnsPerRow: def.columnsPerRow ?? 1,
    renderMode: def.renderMode ?? 'cards',
  });
  return (
    'chartDefinition' in built &&
    _.isEqual(comparable(built.chartDefinition), comparable(chartDef))
  );
};

export default buildEntityInformationTableDefinition;
